import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import UiSwal from '../../../misc/ui-swal';
import {SharedUtilsModule} from '../../../../shared-utils-module';


@Component({
    selector: 'ui-signature-pad',
    templateUrl: './ui-signature-pad.html',
    imports: [
        SharedUtilsModule
    ],
    styleUrl: './ui-signature-pad.scss'
})
export class UiSignaturePadComponent
    implements AfterViewInit, OnChanges {

    // Imagem Base64 recebida do componente que utiliza o canvas.
    @Input() value = '';

    // Permite usar o botão de confirmação como existe nos Autos.
    @Input() showConfirm = true;

    // Permite utilizar o canvas dentro ou fora de um card.
    @Input() showCard = true;

    // Permite ativar o aviso para girar celulares em modo retrato.
    @Input() showOrientationWarning = false;

    // Título apresentado no card.
    @Input() title = 'Assine no espaço abaixo:';

    // Emite a imagem PNG sempre que o valor for atualizado.
    @Output() valueChange = new EventEmitter<string>();

    // Emite separadamente quando o usuário confirma a assinatura.
    @Output() confirmed = new EventEmitter<string>();

    @ViewChild('signatureCanvas')
    canvas?: ElementRef<HTMLCanvasElement>;

    private context?: CanvasRenderingContext2D;
    private drawing = false;
    private currentValue = '';

    ngAfterViewInit(): void {
        const canvasElement = this.canvas?.nativeElement;

        if (!canvasElement) {
            return;
        }

        this.context = canvasElement.getContext('2d') ?? undefined;

        if (!this.context) {
            return;
        }

        // Mesma configuração de traço utilizada nos Autos.
        this.context.lineWidth = 2;
        this.context.strokeStyle = '#000000';
        this.context.lineCap = 'round';
        this.context.lineJoin = 'round';

        this.renderValue(this.value);
    }

    ngOnChanges(changes: SimpleChanges): void {
        /*
         * Se outro componente alterar o Base64, atualizamos o desenho.
         * Antes do AfterViewInit, context ainda não existe.
         */
        if (changes['value'] && this.context) {
            this.renderValue(this.value);
        }
    }

    startDrawing(event: PointerEvent): void {
        // No mouse, aceitamos somente o botão esquerdo.
        if (event.pointerType === 'mouse' && event.button !== 0) {
            return;
        }

        if (!this.context || !this.canvas) {
            return;
        }

        event.preventDefault();

        const canvasElement = this.canvas.nativeElement;
        const position = this.getPointerPosition(event);

        this.drawing = true;

        /*
         * Mantém o evento ligado ao canvas mesmo se o ponteiro
         * sair um pouco da área durante o desenho.
         */
        canvasElement.setPointerCapture(event.pointerId);

        this.context.beginPath();
        this.context.moveTo(position.x, position.y);

        // Também registra um toque simples como um pequeno ponto.
        this.context.lineTo(position.x + 0.01, position.y + 0.01);
        this.context.stroke();
    }

    draw(event: PointerEvent): void {
        if (!this.drawing || !this.context) {
            return;
        }

        event.preventDefault();

        const position = this.getPointerPosition(event);

        this.context.lineTo(position.x, position.y);
        this.context.stroke();
    }

    stopDrawing(event: PointerEvent): void {
        if (!this.drawing || !this.context || !this.canvas) {
            return;
        }

        event.preventDefault();

        this.drawing = false;
        this.context.closePath();

        const canvasElement = this.canvas.nativeElement;

        if (canvasElement.hasPointerCapture(event.pointerId)) {
            canvasElement.releasePointerCapture(event.pointerId);
        }

        if (this.isCanvasBlank()) {
            return;
        }

        this.currentValue = canvasElement.toDataURL('image/png');

        /*
         * Quando não existe botão de confirmação, emitimos
         * automaticamente ao terminar o traço.
         */
        if (!this.showConfirm) {
            this.valueChange.emit(this.currentValue);
        }
    }

    clearSignature(): void {
        if (!this.context || !this.canvas) {
            return;
        }

        const canvasElement = this.canvas.nativeElement;

        this.context.clearRect(
            0,
            0,
            canvasElement.width,
            canvasElement.height
        );

        this.currentValue = '';

        // A tela que utiliza o componente também precisa saber da limpeza.
        this.valueChange.emit('');
    }

    confirmSignature(): void {
        if (
            !this.canvas
            || this.isCanvasBlank()
            || !this.currentValue
        ) {
            UiSwal.fire({
                icon: 'warning',
                title: 'Atenção',
                text: 'Assine no campo indicado antes de confirmar.'
            }).then();

            return;
        }

        this.valueChange.emit(this.currentValue);
        this.confirmed.emit(this.currentValue);
    }

    private getPointerPosition(event: PointerEvent): {
        x: number;
        y: number;
    } {
        const canvasElement = this.canvas!.nativeElement;
        const rectangle = canvasElement.getBoundingClientRect();

        /*
         * Ajusta as coordenadas quando o canvas é redimensionado
         * pelo CSS em computadores ou celulares.
         */
        const scaleX = canvasElement.width / rectangle.width;
        const scaleY = canvasElement.height / rectangle.height;

        return {
            x: (event.clientX - rectangle.left) * scaleX,
            y: (event.clientY - rectangle.top) * scaleY
        };
    }

    private isCanvasBlank(): boolean {
        if (!this.context || !this.canvas) {
            return true;
        }

        const canvasElement = this.canvas.nativeElement;

        const imageData = this.context.getImageData(
            0,
            0,
            canvasElement.width,
            canvasElement.height
        );

        const pixels = new Uint32Array(imageData.data.buffer);

        return !pixels.some(pixel => pixel !== 0);
    }

    private renderValue(imageBase64: string): void {
        if (!this.context || !this.canvas) {
            return;
        }

        const canvasElement = this.canvas.nativeElement;

        this.context.clearRect(
            0,
            0,
            canvasElement.width,
            canvasElement.height
        );

        this.currentValue = imageBase64 ?? '';

        if (!imageBase64) {
            return;
        }

        const image = new Image();
        const expectedValue = imageBase64;

        image.onload = () => {
            /*
             * Evita restaurar uma imagem antiga caso o valor tenha
             * sido limpo enquanto a imagem estava carregando.
             */
            if (this.currentValue !== expectedValue) {
                return;
            }

            this.context?.drawImage(
                image,
                0,
                0,
                canvasElement.width,
                canvasElement.height
            );
        };

        image.src = imageBase64;
    }
}
